import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {MIN_MAX_READER, READER_DATA, READER_NUMBER_DATA} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import {
    AdvanceSearchByFilters,
    CreateHidReader,
    GetAllSiosByHidId
} from '../../../../../reduxToolkit/HID/HidApi';
import {errorMsg} from "../../../../../Apis/functions";
import {TABLES} from "../../../../../Apis/Tables";
import {DEVICE_ACCESS_TYPE_MODEL} from "../../../Devices/Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Custom React hook that manages the logic for creating a HID Reader.
 * Handles form state, validation, dependent dropdowns, and reader creation via Redux.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Function} props.onHide - Function to close the modal after successful creation
 * @param {string|null} [props.controllerId=null] - The HID controller ID (optional)
 * @param {Function} props.setModified - Function to trigger data refresh in the parent component after changes
 *
 * @returns {Object} - Exposes all state variables, form handlers, and helper functions for the Create Reader form
 */
const CreateReaderApp = ({ onHide, controllerId = null, setModified }) => {
    /** --- Hooks --- */
    const { t } = useTranslation();
    const dispatch = useDispatch();

    /** --- Redux Selectors --- */
    const controller = useSelector((state) => state.HidSlice.advanceSearchByFilters);
    const sioIds = useSelector((state) => state.HidSlice.getAllSiosByHidId);

    /** --- Local State --- */
    const [reader, setReader] = useState({ ...READER_DATA, controllerId: controllerId });
    const { handleChange } = HandleChange({ model: reader, setModel: setReader });
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValid, setIsValid] = useState(true);

    /**
     * Loads controller list only if no controllerId is provided.
     * Runs on mount or when controllerId changes.
     */
    useEffect(() => {
        if (!controllerId) {
            dispatch(AdvanceSearchByFilters({ option: TABLES.HID, body: {} }));
        }
    }, [controllerId]);

    /**
     * Fetches available SIOs related to the selected HID controller.
     * Runs whenever `reader.controllerId` changes.
     */
    useEffect(() => {
        if (reader.controllerId) {
            dispatch(GetAllSiosByHidId({ hidId: reader.controllerId }));
        }
    }, [reader.controllerId]);

    /**
     * Validates the "keepOpen" field to ensure it meets the minimum required threshold.
     * - Must be a number >= 5, otherwise marks as invalid.
     */
    useEffect(() => {
        if (reader.keepOpen) {
            const value = parseInt(reader.keepOpen);
            if (!isNaN(value) && value >= MIN_MAX_READER.KEEP_OPEN.start) {
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        } else {
            setIsValid(true);
        }
    }, [reader.keepOpen]);

    /**
     * Handles changes in the "reader number" select field.
     * Automatically adjusts:
     *  - `deviceAccessTypeId`: based on reader position (entry/exit)
     *  - `address`: based on reader number
     *
     * @function
     * @param {Event} event - Change event from the reader number dropdown
     */
    const handleChangeReaderNumber = (event) => {
        const name = event.target.name;
        const value = parseInt(event.target.value, 10);
        handleChange({ target: { value, name } });

        let newAccessType = 0;
        let newAddress = 0;

        switch (value) {
            case READER_NUMBER_DATA.READER_1.id:
            case READER_NUMBER_DATA.READER_2.id:
                newAccessType = DEVICE_ACCESS_TYPE_MODEL.ENTRY.id;
                newAddress = 0;
                break;
            case READER_NUMBER_DATA.READER_3.id:
            case READER_NUMBER_DATA.READER_4.id:
                newAccessType = DEVICE_ACCESS_TYPE_MODEL.EXIT.id;
                newAddress = 1;
                break;
            default:
                break;
        }

        handleChange({ target: { value: newAccessType, name: 'deviceAccessTypeId' } });
        handleChange({ target: { value: newAddress, name: 'address' } });
    };

    /**
     * Submits the new reader form.
     * Validates required fields, dispatches the `CreateHidReader` API call, and shows a success or error toast.
     *
     * @function
     */
    const handleSubmit = () => {
        if (!reader.isValid() || !isValid) {
            if (!reader.isValid()) setEmptyFields(true);
            return;
        }

        const body = reader.toAddEdit();
        dispatch(CreateHidReader(body)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                setModified(true);
                toast.success(t('has_been_created_successfully'));
                resetValues();
                onHide();
            } else errorMsg(code);
        });
    };

    /**
     * Resets the form values to their default state.
     * Keeps the `controllerId` if provided.
     *
     * @function
     */
    const resetValues = () => {
        setReader({ ...READER_DATA, controllerId: (controllerId || null) });
    };

    /** --- Exposed State and Handlers --- */
    return {
        t,
        emptyFields,
        controller,
        sioIds,
        reader,
        setReader,
        handleChange,
        handleChangeReaderNumber,
        handleSubmit,
        resetValues,
        isValid
    };
};

export default CreateReaderApp;
